import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { TwDatePicker } from 'components/DatePicker';
import { Currency } from 'components/Number';
import { CurrencyNumberInput } from 'components/NumberInput';
import moment from 'moment';
import PropTypes from 'prop-types';
import { isGrowthInvestmentComplete, suggestGrowthAmount } from '../calc/suggestGrowthAmount';
import { formatMonthKeyLabel } from '../calc/suggestObligations';
import { normalizeFundingPercentage } from '../calc/pickLatestDeal';

const growthInvestmentLineShape = PropTypes.shape({
  id: PropTypes.string,
  requestReceivedDate: PropTypes.string,
  fundingMonthKey: PropTypes.string,
  growthInvestment: PropTypes.number,
  marketingSpend: PropTypes.number,
});

const SetOffFundingPanel = ({
  setOffEnabled,
  onSetOffEnabledChange,
  lines,
  onLineChange,
  onAddLine,
  onRemoveLine,
  loadingLineIds = {},
  deal,
  growthInvestmentInvalid = false,
  dueDate,
  standalone = false,
}) => {
  const isSetOffActive = standalone || setOffEnabled;
  const fundingPct = normalizeFundingPercentage(deal?.fundingPercentage);

  return (
    <Box>
      {!standalone ? (
        <>
          <FormControl as="fieldset" mb={4}>
            <FormLabel as="legend">Set off repayment against new funding?</FormLabel>
            <RadioGroup
              value={setOffEnabled ? 'yes' : 'no'}
              onChange={(value) => onSetOffEnabledChange(value === 'yes')}
            >
              <Stack direction="row" spacing={6}>
                <Radio value="no">No</Radio>
                <Radio value="yes">Yes</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {setOffEnabled ? (
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              No cash payment required; TERMS &amp; Set-Off Notice will be included.
            </Alert>
          ) : (
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              Cash payment invoice — wire instructions only; no set-off page.
            </Alert>
          )}
        </>
      ) : (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          Set-off notice will be attached to the selected invoice.
        </Alert>
      )}

      {isSetOffActive && (
        <Box>
          {lines.map((line, index) => {
            const suggestion = suggestGrowthAmount({
              marketingSpend: line.marketingSpend,
              fundingPercentage: deal?.fundingPercentage,
            });
            const lineInvalid =
              growthInvestmentInvalid && !isGrowthInvestmentComplete(line.growthInvestment);
            const loading = Boolean(loadingLineIds[line.id]);

            return (
              <Box
                key={line.id || index}
                mb={4}
                pt={index === 0 ? 0 : 4}
                borderTop={index === 0 ? 'none' : '1px solid'}
                borderColor="secondaryGray.200"
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight={700}>
                    {lines.length > 1 ? `Growth investment ${index + 1}` : 'Growth investment'}
                  </Text>
                  {lines.length > 1 ? (
                    <IconButton
                      aria-label="Remove growth investment"
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => onRemoveLine(index)}
                    />
                  ) : null}
                </Flex>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Request received date</FormLabel>
                    <TwDatePicker
                      value={line.requestReceivedDate}
                      onChange={(value) => onLineChange(index, { requestReceivedDate: value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Requested funding month</FormLabel>
                    <Input
                      type="month"
                      value={line.fundingMonthKey}
                      onChange={(event) =>
                        onLineChange(index, { fundingMonthKey: event.target.value })
                      }
                    />
                    <Text fontSize="xs" color="secondaryGray.600" mt={1}>
                      {formatMonthKeyLabel(line.fundingMonthKey)}
                    </Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Marketing spend ({formatMonthKeyLabel(line.fundingMonthKey)})</FormLabel>
                    <Flex align="center" gap={2}>
                      {loading ? (
                        <Spinner size="sm" />
                      ) : line.marketingSpend == null ? (
                        <Text fontWeight={700}>N/A</Text>
                      ) : (
                        <Text fontWeight={700}>
                          <Currency value={line.marketingSpend} maximumFractionDigits={2} />
                        </Text>
                      )}
                    </Flex>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Suggested growth amount</FormLabel>
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      <Text fontWeight={700}>
                        {suggestion.suggestedAmount == null ? (
                          'N/A'
                        ) : (
                          <Currency value={suggestion.suggestedAmount} maximumFractionDigits={2} />
                        )}
                      </Text>
                    )}
                  </FormControl>
                  <FormControl isRequired isInvalid={lineInvalid}>
                    <FormLabel>Growth investment</FormLabel>
                    <CurrencyNumberInput
                      value={line.growthInvestment}
                      onChange={(value) => onLineChange(index, { growthInvestment: value })}
                      showPrefix
                    />
                    {lineInvalid && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        Enter a growth investment amount.
                      </Text>
                    )}
                  </FormControl>
                </SimpleGrid>
              </Box>
            );
          })}

          <Button leftIcon={<AddIcon />} variant="outline" size="sm" onClick={onAddLine}>
            Add growth investment
          </Button>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
            <FormControl>
              <FormLabel>Latest deal funding cap</FormLabel>
              <Badge colorScheme="blue">
                {fundingPct != null ? `${fundingPct.toFixed(0)}% of marketing spend` : 'N/A'}
              </Badge>
            </FormControl>
            <FormControl>
              <FormLabel>Funding date (page 2)</FormLabel>
              <Text>{dueDate ? moment(dueDate).format('MMMM DD, YYYY') : '—'}</Text>
            </FormControl>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
};

SetOffFundingPanel.propTypes = {
  setOffEnabled: PropTypes.bool.isRequired,
  onSetOffEnabledChange: PropTypes.func.isRequired,
  lines: PropTypes.arrayOf(growthInvestmentLineShape).isRequired,
  onLineChange: PropTypes.func.isRequired,
  onAddLine: PropTypes.func.isRequired,
  onRemoveLine: PropTypes.func.isRequired,
  loadingLineIds: PropTypes.object,
  deal: PropTypes.object,
  growthInvestmentInvalid: PropTypes.bool,
  dueDate: PropTypes.string,
  standalone: PropTypes.bool,
};

export default SetOffFundingPanel;
